// libs
import React, { memo, useRef, useState, useEffect } from "react";
import debounce from "lodash/debounce";
import get from "lodash/get";

// global components
import RippleIt from "shared/components/RippleIt";
import { ISearchInputRef } from "shared/components/SearchInput";

// components
import RecentSearches from "./components/RecentSearches";
import AutoSuggest from "./components/AutoSuggest";

// helpers
import { getAkamaiUrl } from "shared/helpers/utils";
import {
    setNewSearch,
    getAutoSuggest,
    defaultSuggestionResponse,
    generateTrackingURLForSearch,
} from "./helpers";

// contstants
import {
    TEXTS,
    CLEAR_IMAGE_URL,
    CLEAR_IMAGE_WIDTH,
    SEARCH_URL,
    SEARCH_TERM_TYPE,
    SEARCH_TYPE,
} from "./constants";

// styles
import { SearchContainer, SearchInput } from "./styles/search";

// analytics
import { updateAnalyticalData } from "./analytics";

// defs
import { IAutoSuggestResponse, ISearchInfo } from "./types";
interface IHeaderSearchProps {
    onHide: VoidFunction;
    showIcon?: boolean;
    initialSearchTerm: string;
}

const MINIMUM_AUTO_SUGGEST_LEN = 1;

function HeaderSearch({ initialSearchTerm, onHide, showIcon = true }: IHeaderSearchProps) {
    const [canShowClear, setShowClear] = useState<boolean>(false);
    const [showSuggestion, setShowSuggestion] = useState(false);
    const [suggestionResponse, setSuggestionResponse] = useState<IAutoSuggestResponse>(
        defaultSuggestionResponse
    );
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
    const searchRef = useRef<ISearchInputRef>(null);

    useEffect(() => {
        let isAPIRequestValid = true;
        const _showSuggestion = (_response: IAutoSuggestResponse) => {
            if (isAPIRequestValid === false) {
                return;
            }
            setShowSuggestion(true);
            setSuggestionResponse(_response);
            updateAnalyticalData(searchTerm, get(_response, "response.suggestions", []));
        };

        if (searchTerm.length >= MINIMUM_AUTO_SUGGEST_LEN) {
            getAutoSuggest(searchTerm).then(_showSuggestion);
        } else {
            setShowSuggestion(false);
        }

        return () => {
            isAPIRequestValid = false;
        };
    }, [searchTerm]);

    const clear = () => {
        if (searchRef.current === null) {
            return;
        }

        searchRef.current.setValue("");
        window.requestAnimationFrame(() => setShowClear(false));
    };

    const onChange = debounce((term: string) => {
        setShowClear(term.length > 0);
        setSearchTerm(term);
    }, 300);

    const onSearchSubmit = (searchFinalTerm: string, searchType?: string) => {
        setNewSearch({ term: searchFinalTerm, type: SEARCH_TERM_TYPE.SEARCH });
        onHide();
        window.location.href = generateTrackingURLForSearch({
            url: `${SEARCH_URL}?q=${encodeURIComponent(searchFinalTerm)}`,
            searchType: searchType || SEARCH_TYPE.MANUAL,
            internalSearchTerm: searchFinalTerm,
        });
    };

    const onRecentSearch = (searchInfo: ISearchInfo) => {
        switch (searchInfo.type) {
            case SEARCH_TERM_TYPE.SEARCH: {
                onSearchSubmit(searchInfo.term, SEARCH_TYPE.RECENT_SEARCH);
                return;
            }

            case SEARCH_TERM_TYPE.PRODUCT: {
                setNewSearch({
                    term: searchInfo.term,
                    type: SEARCH_TERM_TYPE.PRODUCT,
                    url: searchInfo.url,
                });
                onHide();
                window.location.href = generateTrackingURLForSearch({
                    url: searchInfo.url,
                    searchType: SEARCH_TYPE.RECENT_SEARCH,
                    internalSearchTerm: searchInfo.term,
                    typedSearchTerm: searchTerm,
                });

                return;
            }
        }
    };

    return (
        <SearchContainer>
            <div className="sh-container">
                <div className="sh-search">
                    {/* eslint-disable jsx-a11y/no-autofocus */}
                    <SearchInput
                        onChange={onChange}
                        onSubmit={onSearchSubmit}
                        ref={searchRef}
                        showIcon={showIcon}
                        autoFocus
                        data-test="headersearch-input"
                        initialValue={initialSearchTerm}
                    />
                    {canShowClear && (
                        <button
                            className="sh-clear"
                            onClick={clear}
                            aria-label={TEXTS.CLEAR}
                            style={{
                                backgroundImage: `url(${getAkamaiUrl(CLEAR_IMAGE_URL, {
                                    width: CLEAR_IMAGE_WIDTH * 2,
                                })})`,
                            }}
                            data-test="headersearch-clear-btn"
                        />
                    )}
                </div>
                <RippleIt
                    as="button"
                    className="sh-cancel"
                    onClick={onHide}
                    data-test="headersearch-cancel-btn"
                >
                    {TEXTS.CANCEL}
                </RippleIt>
            </div>
            {showSuggestion === false && <RecentSearches onSelect={onRecentSearch} />}
            {showSuggestion && <AutoSuggest data={suggestionResponse} term={searchTerm} />}
        </SearchContainer>
    );
}

export default memo(HeaderSearch);
